import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  Menu,
  X,
  Globe,
  Wrench,
  Search,
  Share2,
  Settings,
  FileText,
  Image,
  MessageSquare,
  HelpCircle,
  Download,
  Newspaper,
  Users,
  Workflow,
  Heart,
  Briefcase,
  ArrowRight,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_agency-square/artifacts/7zqhhxkv_squre%20trigger%20black%20%282%29.png";

const navItems = [
  { name: "Home", path: "/" },
  {
    name: "Solutions",
    path: "/solutions",
    dropdown: [{ name: "Overview", path: "/solutions", icon: Globe }],
  },
  {
    name: "Services",
    path: "/services",
    dropdown: [
      { name: "Website Development", path: "/services/website-development", icon: Globe },
      { name: "Custom Tool Creation", path: "/services/custom-tool-creation", icon: Wrench },
      { name: "SEO Services", path: "/services/seo-services", icon: Search },
      { name: "Social Media Marketing", path: "/services/social-media-marketing", icon: Share2 },
      { name: "Maintenance & Support", path: "/services/maintenance-support", icon: Settings },
    ],
  },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

const megaMenus = {
  Portfolio: {
    items: [
      { name: "Case Studies", path: "/portfolio/case-studies", icon: FileText, description: "In-depth project analyses" },
      { name: "Project Gallery", path: "/portfolio/gallery", icon: Image, description: "Visual showcase of our work" },
      { name: "Client Testimonials", path: "/portfolio/testimonials", icon: MessageSquare, description: "What our clients say" },
    ],
  },
  Resources: {
    items: [
      { name: "FAQs", path: "/resources/faqs", icon: HelpCircle, description: "Common questions answered" },
      { name: "Free Resources", path: "/resources/free", icon: Download, description: "Guides, templates & tools" },
      { name: "News / Press", path: "/resources/news", icon: Newspaper, description: "Latest updates & media" },
      { name: "Blog", path: "/blog", icon: FileText, description: "Insights & articles" },
    ],
  },
  Company: {
    items: [
      { name: "About Us", path: "/company/about", icon: Users, description: "Our story & team" },
      { name: "Our Process", path: "/company/process", icon: Workflow, description: "How we work" },
      { name: "Our Values & Mission", path: "/company/values", icon: Heart, description: "What drives us" },
      { name: "Careers", path: "/company/careers", icon: Briefcase, description: "Join our team" },
    ],
  },
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // Solutions / Services / Portfolio / Resources / Company

  const [activeDropdown, setActiveDropdown] = useState(null); // desktop dropdown
  const [activeMegaMenu, setActiveMegaMenu] = useState(null); // desktop mega

  const location = useLocation();
  const mobilePanelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileOpenSection(null);
    setActiveDropdown(null);
    setActiveMegaMenu(null);
  }, [location]);

  
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    // focus panel for accessibility
    setTimeout(() => {
      mobilePanelRef.current?.focus?.();
    }, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuOpen]);

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const toggleMobileSection = (name) => {
    setMobileOpenSection((cur) => (cur === name ? null : name));
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-border/50 shadow-soft py-2" : "bg-transparent py-4"
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <motion.img
              src={LOGO_URL}
              alt="Square Trigger"
              className="h-9 sm:h-10 w-auto"
              whileHover={{ scale: 1.04 }}
              transition={{ duration: 0.2 }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                    isActivePath(item.path)
                      ? "text-primary bg-accent/40"
                      : "text-foreground/80 hover:text-foreground hover:bg-accent/50"
                  }`}
                >
                  {item.name}
                  {item.dropdown && (
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === item.name ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>

                {/* Dropdown */}
                <AnimatePresence>
                  {item.dropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.18 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-xl shadow-large p-2"
                    >
                      {item.dropdown.map((subItem) => {
                        const Icon = subItem.icon;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-200 hover:bg-accent group"
                          >
                            <div className="p-2 rounded-lg bg-accent group-hover:bg-primary/10 transition-colors">
                              <Icon className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium text-foreground">{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* Mega Menu Triggers */}
            {Object.keys(megaMenus).map((menuName) => (
              <div
                key={menuName}
                className="relative"
                onMouseEnter={() => setActiveMegaMenu(menuName)}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                <button
                  type="button"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                    activeMegaMenu === menuName
                      ? "text-foreground bg-accent/50"
                      : "text-foreground/80 hover:text-foreground hover:bg-accent/50"
                  }`}
                >
                  {menuName}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      activeMegaMenu === menuName ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Mega Menu */}
                <AnimatePresence>
                  {activeMegaMenu === menuName && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.18 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[420px] bg-card border border-border rounded-xl shadow-large p-4"
                    >
                      <div className="grid grid-cols-1 gap-1">
                        {megaMenus[menuName].items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.name}
                              to={item.path}
                              className="flex items-start gap-3 px-3 py-3 rounded-lg transition-all duration-200 hover:bg-accent group"
                            >
                              <div className="p-2 rounded-lg bg-accent group-hover:bg-primary/10 transition-colors flex-shrink-0">
                                <Icon className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <span className="font-medium text-foreground block">{item.name}</span>
                                <span className="text-xs text-muted-foreground">{item.description}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button variant="gradient" asChild className="group">
              <Link to="/get-started" className="inline-flex items-center gap-2">
                Get Started
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden fixed inset-0 bg-black/35 backdrop-blur-[2px]"
                onClick={() => setMobileMenuOpen(false)}
              />

              {/* Panel */}
              <motion.div
                ref={mobilePanelRef}
                tabIndex={-1}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.25 }}
                className="lg:hidden fixed right-0 top-0 h-[100dvh] w-[92vw] max-w-[420px] bg-card border-l border-border shadow-large"
              >
                {/* Panel Header */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <img src={LOGO_URL} alt="Square Trigger" className="h-8 w-auto" />
                  </div>
                  <button
                    aria-label="Close menu"
                    className="p-2 rounded-lg hover:bg-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="h-[calc(100dvh-64px)] overflow-y-auto px-3 py-3">
                  {/* Primary Links */}
                  <div className="space-y-2">
                    {navItems.map((item) => {
                      const hasDropdown = !!item.dropdown;

                      if (!hasDropdown) {
                        return (
                          <Link
                            key={item.name}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center justify-between px-3 py-3 rounded-xl transition-colors font-medium ${
                              isActivePath(item.path) ? "bg-accent text-foreground" : "text-foreground hover:bg-accent"
                            }`}
                          >
                            <span>{item.name}</span>
                            <ArrowRight className="h-4 w-4 opacity-60" />
                          </Link>
                        );
                      }

                      return (
                        <div key={item.name} className="rounded-xl border border-border/60 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleMobileSection(item.name)}
                            className="w-full flex items-center justify-between px-3 py-3 text-left font-medium hover:bg-accent transition-colors"
                          >
                            <span className="text-foreground">{item.name}</span>
                            <ChevronDown
                              className={`h-5 w-5 transition-transform ${
                                mobileOpenSection === item.name ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          <AnimatePresence initial={false}>
                            {mobileOpenSection === item.name && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.18 }}
                                className="border-t border-border/60 bg-background/40"
                              >
                                <div className="p-2 space-y-1">
                                  <Link
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                      isActivePath(item.path)
                                        ? "bg-accent text-foreground"
                                        : "text-foreground/90 hover:bg-accent"
                                    }`}
                                  >
                                    {item.name} Overview
                                  </Link>

                                  {item.dropdown.map((subItem) => {
                                    const Icon = subItem.icon;
                                    return (
                                      <Link
                                        key={subItem.name}
                                        to={subItem.path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                          isActivePath(subItem.path)
                                            ? "bg-accent text-foreground"
                                            : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                        }`}
                                      >
                                        <span className="p-2 rounded-lg bg-accent/70">
                                          <Icon className="h-4 w-4 text-primary" />
                                        </span>
                                        <span className="font-medium">{subItem.name}</span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  {/* Mega Menu Sections */}
                  <div className="mt-4 space-y-2">
                    {Object.entries(megaMenus).map(([menuName, menu]) => (
                      <div key={menuName} className="rounded-xl border border-border/60 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleMobileSection(menuName)}
                          className="w-full flex items-center justify-between px-3 py-3 text-left font-medium hover:bg-accent transition-colors"
                        >
                          <span className="text-foreground">{menuName}</span>
                          <ChevronDown
                            className={`h-5 w-5 transition-transform ${
                              mobileOpenSection === menuName ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {mobileOpenSection === menuName && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.18 }}
                              className="border-t border-border/60 bg-background/40"
                            >
                              <div className="p-2 space-y-1">
                                {menu.items.map((item) => {
                                  const Icon = item.icon;
                                  return (
                                    <Link
                                      key={item.name}
                                      to={item.path}
                                      onClick={() => setMobileMenuOpen(false)}
                                      className={`flex items-start gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                        isActivePath(item.path)
                                          ? "bg-accent text-foreground"
                                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                      }`}
                                    >
                                      <span className="p-2 rounded-lg bg-accent/70 mt-0.5">
                                        <Icon className="h-4 w-4 text-primary" />
                                      </span>
                                      <span className="leading-tight">
                                        <span className="font-medium block text-foreground">{item.name}</span>
                                        <span className="text-xs text-muted-foreground">{item.description}</span>
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="mt-5 pt-4 border-t border-border space-y-2 px-1 pb-6">
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                        Contact Us
                      </Link>
                    </Button>
                    <Button variant="gradient" className="w-full" asChild>
                      <Link to="/get-started" onClick={() => setMobileMenuOpen(false)}>
                        Get Started
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};